import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../../context/AuthProvider";
import { AppContext } from "../../context/AppProvider";
import { fetchWithInterceptor } from "../../utils/fetchWithInterceptor";

export default function ScanDialog({ open, onClose, currentQueueId }) {
  const authContext = useContext(AuthContext);
  const appContext = useContext(AppContext);

  const inputRef = useRef();

  const [scanInput, setScanInput] = useState("");
  const [scannedProduct, setScannedProduct] = useState(null);

  const handleProductSearch = async (query) => {
    try {
      const res = await fetchWithInterceptor(`/api/product-labels/products/barcode/${encodeURIComponent(query)}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
      });

      if (res.status === 404) {
        setScannedProduct(null);
        appContext.showMessage("No matches found for that barcode/sku.", "error");
        return;
      }

      if (!res.ok) {
        throw new Error("Product not found");
      }

      const data = await res.json();
      setScannedProduct({ ...data, price: data.calculatedPrice });
    } catch (err) {
      setScannedProduct(null);
      appContext.showMessage("Error searching for product.", "error");
    }
  };

  const handleAddScannedToQueue = async () => {
    if (!scannedProduct || !currentQueueId) return;

    try {
      const addRes = await fetchWithInterceptor("/api/product-labels/add-to-queue", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
        body: JSON.stringify({
          printFileId: currentQueueId,
          productName: scannedProduct.productName,
          price: scannedProduct.price ?? 0,
          binNumber: scannedProduct.binNumber ?? null,
          barcode: scannedProduct.barcode ?? null,
          sku: scannedProduct.sku ?? null,
        }),
      });

      if (!addRes.ok) throw new Error("Failed to add product to queue");

      appContext.showMessage("Product added to queue", "success");
      setScanInput("");
      setScannedProduct(null);
      focusOnBarcode()
    } catch (err) {
      console.error(err);
      appContext.showMessage(err.message || "Error adding product", "error");
    }
  };

  const focusOnBarcode = () => {
    setTimeout(() => {
      inputRef.current?.focus()
    }, 300)
  }

  const handleDismiss = () => {
    setScannedProduct(null);
    setScanInput("");
    focusOnBarcode()
  }

  const handleGoBack = () => {
    onClose();
    setScanInput("");
    setScannedProduct(null);
  }

  useEffect(() => {
    if (open) {
      focusOnBarcode()
    }
  }, [open])

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
        setScanInput("");
        setScannedProduct(null);
      }}
      fullScreen
    >
      <DialogTitle>
        {`Scan ${currentQueueId ? ` — Queue #${currentQueueId}` : ""}`}
        <Button
          onClick={handleGoBack}
          sx={{ position: "absolute", top: 8, right: 16 }}
          variant="text"
          size="small"
        >
          Go Back
        </Button>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }} direction="column">
          <Grid xs={12}>
            <TextField
              fullWidth
              label="Barcode or SKU"
              variant="outlined"
              value={scanInput}
              inputRef={inputRef}
              onChange={(e) => {
                const value = e.target.value;
                setScanInput(value);
                if (value.length >= 3) {
                  handleProductSearch(value);
                }
              }}
            />
          </Grid>

          {scannedProduct && (
            <Grid item>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <List dense>
                  <ListItem disableGutters>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "space-between",
                        width: "100%",
                        gap: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {scannedProduct.productName}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        ${Number(scannedProduct.price).toFixed(2)}
                      </Typography>
                    </Box>
                  </ListItem>

                  <Divider sx={{ my: 1 }} />

                  <ListItem disableGutters>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          <Typography
                            component="span"
                            sx={{ fontWeight: 700, mr: 0.5 }}
                            display="inline"
                          >
                            BIN:{" "}
                          </Typography>
                          {scannedProduct.binNumber || "-"}
                        </Typography>
                      }
                    />
                  </ListItem>

                  <ListItem disableGutters>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          <Typography
                            component="span"
                            sx={{ fontWeight: 700, mr: 0.5 }}
                            display="inline"
                          >
                            Barcode:{" "}
                          </Typography>
                          {scannedProduct.barcode}
                        </Typography>
                      }
                    />
                  </ListItem>

                  <ListItem disableGutters>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          <Typography
                            component="span"
                            sx={{ fontWeight: 700, mr: 0.5 }}
                            display="inline"
                          >
                            SKU:{" "}
                          </Typography>{" "}
                          {scannedProduct.sku}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          px: 1,
          pb: 1.5,
          pt: 0,
          display: "flex",
          width: "100%",
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleAddScannedToQueue}
          disabled={!scannedProduct}
          fullWidth
          sx={{ flex: 1, minWidth: 0 }}
        >
          Add to Queue
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={handleDismiss}
          disabled={!scannedProduct}
          fullWidth
          sx={{ flex: 1, minWidth: 0 }}
        >
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
}
